import { Button } from '@forwardflow/ui'
import { PreviousBillings } from './PreviousBillings'
import { NextBillingCycle } from './NextBillingCycle'
import { SavedCards } from './SavedCards'
import { useGetUserSubscription } from '@forwardflow/hooks/customHooks'

import { PauseSubscriptionModal } from '@forwardflow/modals/PauseSubscriptionModal'
import { usePauseSubscriptionModalStore } from '@forwardflow/store/modal/pauseSubscriptionModal'
import { useEffect, useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { UpdatePaymentModal } from '@forwardflow/modals/UpdatePaymentModal'
import { Skeleton } from 'antd'
import SubscriptionService from '@forwardflow/services/SubscriptionService'
import { useAppStore } from '@forwardflow/store'
import { SegmentControl } from '@forwardflow/ui-v2'
import { useGetMonthlyBudgets } from '@forwardflow/hooks/customHooks/useGetMonthlyBudgets'
import { ChangeNewLeadsLimitModal } from '@forwardflow/modals/ChangeNewLeadsLimitModal'
import { ChangeColdLeadsLimitModal } from '@forwardflow/modals/ChangeColdLeadsLimitModal'
import { PurchaseCreditModal } from '@forwardflow/modals/PurchaseCreditModal'
import { usePurchaseCreditModalStore } from '@forwardflow/store/modal'
import OrganizationService from '@forwardflow/services/OrganizationService'
import { message } from 'antd'
import { UsageTab } from './UsageTab'
import dayjs from 'dayjs'

type TBillingTab = 'subscription' | 'usage' | 'payment-billing'

const fmt = (n: number | null | undefined) => (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const BillingAndSubscription = () => {
   const { data, isLoading, mutate } = useGetUserSubscription()
   const { openPauseSubscriptionModal } = usePauseSubscriptionModalStore()
   const { currentOrganization } = useAppStore()
   const [seats, setSeats] = useState(1)
   const [originalSeats, setOriginalSeats] = useState(1)
   const [isUpdatingSeats, setIsUpdatingSeats] = useState(false)
   const [searchParams, setSearchParams] = useSearchParams()

   const VALID_TABS: TBillingTab[] = ['subscription', 'usage', 'payment-billing']
   const activeTab = useMemo(() => {
      const tab = searchParams.get('tab') as TBillingTab
      return VALID_TABS.includes(tab) ? tab : 'subscription'
   }, [searchParams])

   const setActiveTab = (tab: TBillingTab) => {
      setSearchParams({ tab }, { replace: true })
   }

   const [subscriptionStatus, setSubscriptionStatus] = useState(
      data?.organizationSubscription?.status
   )

   useEffect(() => {
      setSubscriptionStatus(data?.organizationSubscription?.status)
   }, [data?.organizationSubscription?.status])

   // Initialize seats from subscription data
   useEffect(() => {
      if (data?.organizationSubscription) {
         const currentSeats = data.organizationSubscription.seatCounts || 1
         setSeats(currentSeats)
         setOriginalSeats(currentSeats)
      }
   }, [data?.organizationSubscription])

   const toggleSubscriptionStatus = async () => {
      openPauseSubscriptionModal()
   }

   const handleIncrementSeat = () => {
      const maxSeats =
         data?.organizationSubscription?.pricingTier?.maxSeats || 100
      if (seats < maxSeats) {
         setSeats((prev) => prev + 1)
      }
   }

   const handleDecrementSeat = () => {
      const minSeats =
         data?.organizationSubscription?.pricingTier?.minSeats || 1
      if (seats > minSeats) {
         setSeats((prev) => prev - 1)
      }
   }

   const handleSaveSeats = async () => {
      if (!currentOrganization || isUpdatingSeats || seats === originalSeats)
         return

      try {
         setIsUpdatingSeats(true)
         const seatDifference = seats - originalSeats

         if (seatDifference > 0) {
            // Add seats
            await SubscriptionService.addSeats(
               currentOrganization,
               seatDifference
            )
         } else {
            // Remove seats
            await SubscriptionService.removeSeats(
               currentOrganization,
               Math.abs(seatDifference)
            )
         }

         setOriginalSeats(seats)
         // Refresh subscription data
         mutate()
      } catch (error) {
         // Handle error silently or show notification
         // Revert to original seats on error
         setSeats(originalSeats)
      } finally {
         setIsUpdatingSeats(false)
      }
   }

   const handleCancelSeats = () => {
      setSeats(originalSeats)
   }

   const hasSeatsChanged = seats !== originalSeats

   const { data: budgetData, mutate: mutateBudgets } = useGetMonthlyBudgets()
   const { openPurchaseCreditModal } = usePurchaseCreditModalStore()
   const [isNewLeadsModalOpen, setIsNewLeadsModalOpen] = useState(false)
   const [isColdLeadsModalOpen, setIsColdLeadsModalOpen] = useState(false)
   const [isSavingBudget, setIsSavingBudget] = useState(false)

   const handleSaveNewLeadBudget = async (newLimit: number) => {
      if (!budgetData || isSavingBudget) return
      try {
         setIsSavingBudget(true)
         await OrganizationService.updateMonthlyBudgets({
            newLeadBudget: newLimit,
            reactivateLeadBudget: budgetData.reactivateLeadBudget,
         })
         await mutateBudgets()
         message.success('New leads budget updated')
         setIsNewLeadsModalOpen(false)
      } catch {
         message.error('Failed to update budget')
      } finally {
         setIsSavingBudget(false)
      }
   }

   const handleSaveColdLeadBudget = async (newLimit: number) => {
      if (!budgetData || isSavingBudget) return
      try {
         setIsSavingBudget(true)
         await OrganizationService.updateMonthlyBudgets({
            newLeadBudget: budgetData.newLeadBudget,
            reactivateLeadBudget: newLimit,
         })
         await mutateBudgets()
         message.success('Cold leads budget updated')
         setIsColdLeadsModalOpen(false)
      } catch {
         message.error('Failed to update budget')
      } finally {
         setIsSavingBudget(false)
      }
   }


   const BASE_SEAT_COST = 97
   const ADDITIONAL_SEAT_COST = 50
   const PHONE_NUMBER_COST = 1.5

   // Calculate seat costs (current vs new)
   const currentSeatsCost = BASE_SEAT_COST + (originalSeats - 1) * ADDITIONAL_SEAT_COST
   const seatsCost = BASE_SEAT_COST + (seats - 1) * ADDITIONAL_SEAT_COST
   const estimatedCost = seatsCost
   const costDifference = estimatedCost - currentSeatsCost
   const phoneNumbersCost = data?.phoneNumbers?.price || 0
   const rechargeAmount = (budgetData?.newLeadBudget || 0) + (budgetData?.reactivateLeadBudget || 0)
   const remainingCredits = data?.creditInfo?.remainingCredits ?? 0
   const renewalDate = data?.nextBillingCycle?.currentPeriodEnd
      ? dayjs(data.nextBillingCycle.currentPeriodEnd).format('MMM D, YYYY')
      : ''

   const renderSubscriptionTab = () => (
      <>
         {isLoading ? (
            <div className="flex flex-col gap-4">
               <Skeleton active />
               <Skeleton active />
            </div>
         ) : (
            <>
               {/* Info Banner */}
               <div className="bg-[#474747]/10 flex items-center gap-3 border border-white/10 rounded-xl py-4 px-6">
                  <span className="text-lg">🚀</span>
                  <p className="text-white/70 text-sm">
                     Your subscription, phone numbers, and credit recharge are all billed together at the start of each month.
                  </p>
               </div>

               {/* Plan Card */}
               <div className="bg-[#474747]/10 rounded-xl border border-white/10 overflow-hidden">
                  <div className="flex items-center justify-between py-5 px-6 bg-white/[0.02]">
                     <div>
                        <p className="text-white font-semibold text-base">Plan</p>
                        <p className="text-white/40 text-sm mt-0.5">
                           Charged monthly{renewalDate ? ` · renews ${renewalDate}` : ''}
                        </p>
                     </div>
                  </div>
                  <div className="border-t border-white/10" />

                  {/* Users Row */}
                  <div className="flex items-center justify-between py-5 px-6">
                     <div>
                        <p className="text-white/80 text-sm font-medium">Users</p>
                        <p className="text-white/40 text-xs">${BASE_SEAT_COST} for first seat + ${ADDITIONAL_SEAT_COST} per additional seat / month</p>
                     </div>
                     <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                           <button
                              onClick={handleDecrementSeat}
                              disabled={
                                 seats <=
                                    (data?.organizationSubscription?.pricingTier
                                       ?.minSeats || 1) || isUpdatingSeats
                              }
                              className="flex items-center justify-center w-8 h-8 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                           >
                              <span className="text-white">−</span>
                           </button>
                           <span className="text-white font-medium min-w-[28px] text-center">{seats}</span>
                           <button
                              onClick={handleIncrementSeat}
                              disabled={
                                 seats >=
                                    (data?.organizationSubscription?.pricingTier
                                       ?.maxSeats || 100) || isUpdatingSeats
                              }
                              className="flex items-center justify-center w-8 h-8 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                           >
                              <span className="text-white">+</span>
                           </button>
                        </div>
                        <span className="text-white font-medium">${fmt(seatsCost)}<span className="text-white/40 text-sm font-normal">/mo</span></span>
                     </div>
                  </div>

                  {hasSeatsChanged && (
                     <div className="px-6 pb-4">
                        <div className="bg-white/[0.03] border border-white/10 rounded-lg p-4 space-y-2">
                           <div className="flex items-center justify-between text-sm">
                              <span className="text-white/60">Current ({originalSeats} user{originalSeats !== 1 ? 's' : ''})</span>
                              <span className="text-white">${fmt(currentSeatsCost)}/mo</span>
                           </div>
                           <div className="flex items-center justify-between text-sm">
                              <span className="text-white/60">New ({seats} user{seats !== 1 ? 's' : ''})</span>
                              <span className="text-white">${fmt(estimatedCost)}/mo</span>
                           </div>
                           <div className="border-t border-white/10 pt-2 flex items-center justify-between">
                              <span className="text-white font-medium text-sm">{costDifference > 0 ? 'Additional' : 'Savings'}</span>
                              <span className={`font-semibold text-sm ${costDifference > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                                 {costDifference > 0 ? '+' : ''}${fmt(Math.abs(costDifference))}/mo
                              </span>
                           </div>
                        </div>
                        <div className="flex items-center justify-end gap-3 mt-3">
                           <button
                              onClick={handleCancelSeats}
                              disabled={isUpdatingSeats}
                              className="px-4 py-2 rounded-lg bg-white/[0.05] hover:bg-white/10 border border-white/10 disabled:opacity-50 transition-colors text-white text-sm font-medium"
                           >
                              Cancel
                           </button>
                           <button
                              onClick={handleSaveSeats}
                              disabled={isUpdatingSeats}
                              className="px-4 py-2 rounded-lg bg-primary hover:bg-primary/80 disabled:opacity-50 transition-colors text-white text-sm font-medium"
                           >
                              {isUpdatingSeats ? 'Saving...' : 'Save Changes'}
                           </button>
                        </div>
                     </div>
                  )}

                  <div className="border-t border-white/10" />

                  {/* Phone Numbers Row */}
                  <div className="flex items-center justify-between py-5 px-6">
                     <div>
                        <p className="text-white/80 text-sm font-medium">Phone Numbers</p>
                        <p className="text-white/40 text-xs">${fmt(PHONE_NUMBER_COST)} per number / month</p>
                     </div>
                     <div className="flex items-center gap-3">
                        <span className="text-primary font-medium">{data?.phoneNumbers?.count} numbers</span>
                        <span className="text-white/30">·</span>
                        <span className="text-white font-medium">${fmt(phoneNumbersCost)}<span className="text-white/40 text-sm font-normal">/mo</span></span>
                     </div>
                  </div>
               </div>

               {/* Monthly Credit Recharge Card */}
               <div className="bg-[#474747]/10 rounded-xl border border-white/10 overflow-hidden">
                  <div className="flex items-center justify-between py-5 px-6 bg-white/[0.02]">
                     <div>
                        <p className="text-white font-semibold text-base">Monthly Credit Recharge</p>
                        <p className="text-white/40 text-sm mt-0.5">Added to your balance automatically at the start of every month</p>
                     </div>
                     <span className="text-primary font-semibold text-lg">${fmt(rechargeAmount)}<span className="text-white/40 text-sm font-normal">/mo</span></span>
                  </div>
                  <div className="border-t border-white/10" />

                  {/* New Leads Nurture */}
                  <div className="flex items-center justify-between py-5 px-6 border-b border-white/[0.06]">
                     <p className="text-white/80 text-sm font-medium">New Leads Nurture</p>
                     <div className="flex items-center gap-4">
                        <span className="text-white font-medium">${fmt(budgetData?.newLeadBudget)}<span className="text-white/40 text-sm font-normal">/mo</span></span>
                        <button
                           onClick={() => setIsNewLeadsModalOpen(true)}
                           className="px-3 py-1.5 text-xs text-white bg-white/[0.05] hover:bg-white/10 border border-white/10 rounded-lg transition-colors"
                        >
                           Change
                        </button>
                     </div>
                  </div>

                  {/* Cold Leads Revival */}
                  <div className="flex items-center justify-between py-5 px-6">
                     <p className="text-white/80 text-sm font-medium">Cold Leads Revival</p>
                     <div className="flex items-center gap-4">
                        <span className="text-white font-medium">${fmt(budgetData?.reactivateLeadBudget)}<span className="text-white/40 text-sm font-normal">/mo</span></span>
                        <button
                           onClick={() => setIsColdLeadsModalOpen(true)}
                           className="px-3 py-1.5 text-xs text-white bg-white/[0.05] hover:bg-white/10 border border-white/10 rounded-lg transition-colors"
                        >
                           Change
                        </button>
                     </div>
                  </div>
               </div>

               {/* Credit Balance Card */}
               <div className="bg-[#474747]/10 rounded-xl border border-white/10 overflow-hidden">
                  <div className="flex items-center justify-between py-5 px-6">
                     <div>
                        <p className="text-white font-semibold text-base">Credit Balance</p>
                        <button
                           onClick={openPurchaseCreditModal}
                           className="text-primary/70 hover:text-primary text-xs font-medium transition-colors mt-0.5"
                        >
                           + Add Credits
                        </button>
                     </div>
                     <div className="text-right">
                        <p className="text-white/40 text-xs uppercase tracking-wider">Available</p>
                        <p className="text-white font-semibold text-2xl">${fmt(remainingCredits)}</p>
                        {remainingCredits < 20 && (
                           <p className="text-orange-400 text-xs mt-0.5">Running low</p>
                        )}
                     </div>
                  </div>
               </div>

               {/* Monthly Bill Total Card */}
               <div className="bg-[#474747]/10 rounded-xl border border-white/10 overflow-hidden">
                  <div className="flex items-center justify-between py-5 px-6 bg-white/[0.02]">
                     <p className="text-white font-semibold text-base">Monthly Bill Total</p>
                     {renewalDate && (
                        <p className="text-white/40 text-sm">What gets charged on {renewalDate}</p>
                     )}
                  </div>
                  <div className="border-t border-white/10" />
                  <div className="flex items-center justify-between py-4 px-6 border-b border-white/10">
                     <span className="text-white/70 text-sm">Subscription ({seats} user{seats !== 1 ? 's' : ''})</span>
                     <span className="text-white font-medium">${fmt(seatsCost)}</span>
                  </div>
                  <div className="flex items-center justify-between py-4 px-6 border-b border-white/10">
                     <span className="text-white/70 text-sm">Phone Numbers ({data?.phoneNumbers?.count})</span>
                     <span className="text-white font-medium">${fmt(phoneNumbersCost)}</span>
                  </div>
                  <div className="flex items-center justify-between py-4 px-6 border-b border-white/10">
                     <span className="text-white/70 text-sm">Credit Recharge</span>
                     <span className="text-white font-medium">${fmt(rechargeAmount)}</span>
                  </div>
                  <div className="flex items-center justify-between py-5 px-6">
                     <span className="text-white font-semibold">Total</span>
                     <span className="text-primary font-semibold text-xl">${fmt(seatsCost + phoneNumbersCost + rechargeAmount)}<span className="text-white/40 text-sm font-normal">/mo</span></span>
                  </div>
               </div>
            </>
         )}

         {/* Pause/Resume Subscription */}
         <div className="bg-[#474747]/10 rounded-xl border border-white/10 overflow-hidden">
            <div className="flex items-center justify-between py-4 px-6 bg-white/[0.02] border-b border-white/10">
               <span className="text-white font-semibold text-base">
                  {subscriptionStatus === 'active' ? 'Pause' : 'Resume'} Subscription
               </span>
               <span className="text-white/40 text-sm">
                  Temporarily stop all AI activity. Your data and settings are kept safe.
               </span>
            </div>
            <div className="flex items-center justify-between py-5 px-6">
               <div>
                  <p className="text-white/60 text-sm">
                     Pausing stops all calls, messages, and lead nurturing until you resume.
                  </p>
                  <p className="text-white/40 text-xs mt-1">
                     Your billing cycle continues and resumes normally when you unpause.
                  </p>
               </div>
               <Button
                  onClick={toggleSubscriptionStatus}
                  className={`!h-10 !rounded px-7 !mt-0 !w-28 shrink-0 ${
                     subscriptionStatus === 'active'
                        ? '!bg-danger/20 hover:!bg-danger/50 !border-danger'
                        : '!bg-primary/10'
                  }`}
               >
                  {subscriptionStatus === 'active' ? 'Pause' : 'Resume'}
               </Button>
            </div>
         </div>
      </>
   )

   const renderUsageTab = () => <UsageTab />

   const renderPaymentBillingTab = () => (
      <>
         <div className="flex gap-8">
            <SavedCards />
            <NextBillingCycle />
         </div>
         <PreviousBillings />
      </>
   )

   const renderTabContent = () => {
      switch (activeTab) {
         case 'subscription':
            return renderSubscriptionTab()
         case 'usage':
            return renderUsageTab()
         case 'payment-billing':
            return renderPaymentBillingTab()
         default:
            return null
      }
   }

   const [isScrolled, setIsScrolled] = useState(false)

   const handleContentScroll = (e: React.UIEvent<HTMLDivElement>) => {
      setIsScrolled(e.currentTarget.scrollTop > 0)
   }

   return (
      <div className="flex flex-col flex-1 h-full overflow-hidden text-white">
         <div className={`sticky top-0 z-20 bg-inherit px-10 pt-8 pb-6 transition-shadow duration-200 ${isScrolled ? 'shadow-[0_4px_20px_rgba(0,0,0,0.4)]' : ''}`}>
            <h1 className="text-2xl font-bold">Billing & Subscription</h1>
            <p className="text-white/50 text-sm mt-1">Manage your plan, credits, and payment details</p>
            <div className="mt-6">
               <SegmentControl
                  value={activeTab}
                  onChange={(value) => setActiveTab(value as TBillingTab)}
                  data={[
                     { label: '📋 Subscription', value: 'subscription' },
                     { label: '⚡ Usage', value: 'usage' },
                     { label: '💳 Payment & Billing', value: 'payment-billing' },
                  ]}
               />
            </div>
         </div>
         <div className="flex-1 overflow-y-auto" onScroll={handleContentScroll}>
            <div className="flex flex-col px-10 gap-8 pb-8">
               {renderTabContent()}
            </div>
         </div>
         <PauseSubscriptionModal />
         <UpdatePaymentModal />
         <PurchaseCreditModal />
         <ChangeNewLeadsLimitModal
            open={isNewLeadsModalOpen}
            currentLimit={budgetData?.newLeadBudget || 0}
            onClose={() => setIsNewLeadsModalOpen(false)}
            onSave={handleSaveNewLeadBudget}
         />
         <ChangeColdLeadsLimitModal
            open={isColdLeadsModalOpen}
            currentLimit={budgetData?.reactivateLeadBudget || 0}
            onClose={() => setIsColdLeadsModalOpen(false)}
            onSave={handleSaveColdLeadBudget}
         />
      </div>
   )
}

export default BillingAndSubscription
